import { Button } from '../design/components'
import { useOnline, useServiceWorker } from '../pwa/useServiceWorker'
import styles from './StatusBar.module.css'

/**
 * The two things the app has to say about *itself* rather than about the game.
 *
 * Both are sticky under the header and both are absent almost always, which is the point:
 * they are the only chrome that appears without being asked for, so each one has to earn
 * the row it takes on a 390px screen.
 *
 * **Offline.** Everything except the app shell and the reference lists comes from the API,
 * so offline the screens still render and every write fails. Saying so up front is the
 * difference between "the app is broken" and "the tunnel is". It is `navigator.onLine`,
 * which reports the network interface rather than the internet — so this explains failures
 * and never prevents an attempt.
 *
 * **An update is waiting.** A new build has installed and is deliberately not taking over
 * on its own; swapping the JavaScript under someone mid-edit is not an improvement. The
 * reload is theirs to press.
 */
export function StatusBar() {
  const online = useOnline()
  const { updateReady, update } = useServiceWorker()

  if (online && !updateReady) return null

  return (
    // `role="status"` rather than `alert`: neither of these is urgent, and an alert
    // interrupts whatever a screen reader was in the middle of saying.
    <div className={styles.bar} role="status">
      {!online && (
        <p className={styles.line}>
          <span className={styles.dot} aria-hidden="true" />
          Offline — you can read what is already loaded. Changes will not save.
        </p>
      )}

      {updateReady && (
        <p className={styles.line}>
          <span className={styles.lineText}>A new version of Harrowgate is ready.</span>
          <Button variant="secondary" onClick={update}>
            Reload
          </Button>
        </p>
      )}
    </div>
  )
}

/**
 * Which build this is, in the smallest type the design system has.
 *
 * "Which code is the tablet at the table running" is a question a commit sha answers and a
 * version number nobody remembered to bump does not. It sits at the bottom of every
 * screen, below the last card, where it is findable and never in the way.
 */
export function BuildInfo() {
  const built = new Date(__BUILD_TIME__)

  return (
    <p className={styles.build}>
      Harrowgate <span className={styles.buildId}>{__BUILD_ID__}</span>
      {!Number.isNaN(built.getTime()) && (
        <>
          {' · '}
          <time dateTime={__BUILD_TIME__}>{built.toISOString().slice(0, 10)}</time>
        </>
      )}
    </p>
  )
}
